// Coordinator client. Same-origin `/api` by default; Settings › Advanced can
// point it elsewhere (that coordinator must then allow this origin with CORS).
// Every request carries X-Kumio-Client, which the coordinator requires on
// state-changing calls: a cross-site form cannot set it, so it doubles as CSRF
// protection on top of the SameSite session cookie.

import { useSettings } from '../state/settings';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code: string | null = null,
  ) {
    super(message);
  }
}

export const apiBase = () => (useSettings.getState().coordinatorOverride.trim() || '/api').replace(/\/$/, '');

export async function api<T>(path: string, opts: RequestInit & { json?: unknown; timeoutMs?: number } = {}): Promise<T> {
  const { json, timeoutMs = 15000, ...init } = opts;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  if (init.signal) init.signal.addEventListener('abort', () => ctrl.abort(), { once: true });
  let res: Response;
  try {
    res = await fetch(apiBase() + path, {
      credentials: 'include',
      ...init,
      signal: ctrl.signal,
      headers: {
        'x-kumio-client': '1',
        ...(json !== undefined ? { 'content-type': 'application/json' } : {}),
        ...(init.headers || {}),
      },
      body: json !== undefined ? JSON.stringify(json) : init.body,
    });
  } catch (e) {
    throw new ApiError(ctrl.signal.aborted && !init.signal?.aborted ? 'The coordinator did not answer in time.' : 'The coordinator is unreachable.', 0, 'offline');
  } finally {
    clearTimeout(timer);
  }
  let body: unknown = null;
  const text = await res.text();
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = null;
  }
  if (!res.ok) {
    const b = body as { error?: string; code?: string } | null;
    if (res.status >= 500 && !b?.error) throw new ApiError('The coordinator is unreachable.', res.status, 'offline');
    throw new ApiError(b?.error || `Request failed (${res.status}).`, res.status, b?.code ?? null);
  }
  return body as T;
}

/**
 * Read a Server-Sent Events stream (GET or POST). Each `data:` line is JSON.
 * Resolves when the stream ends; rejects on HTTP errors.
 */
export async function sse(
  path: string,
  onEvent: (ev: Record<string, unknown>) => void,
  opts: { method?: 'GET' | 'POST'; json?: unknown; signal?: AbortSignal } = {},
): Promise<void> {
  const res = await fetch(apiBase() + path, {
    method: opts.method ?? 'GET',
    credentials: 'include',
    signal: opts.signal,
    headers: {
      'x-kumio-client': '1',
      accept: 'text/event-stream',
      ...(opts.json !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    body: opts.json !== undefined ? JSON.stringify(opts.json) : undefined,
  }).catch(() => {
    throw new ApiError('The coordinator is unreachable.', 0, 'offline');
  });
  if (!res.ok || !res.body) {
    let msg = `Request failed (${res.status}).`;
    let code: string | null = null;
    try {
      const b = await res.json();
      msg = b.error || msg;
      code = b.code ?? null;
    } catch {
      /* not JSON */
    }
    throw new ApiError(msg, res.status, code);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, i);
      buf = buf.slice(i + 2);
      for (const line of chunk.split('\n')) {
        if (!line.startsWith('data:')) continue;
        try {
          onEvent(JSON.parse(line.slice(5).trim()));
        } catch {
          /* ignore malformed event */
        }
      }
    }
  }
}
